import { useState } from "react";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Button } from "@/shared/components/button";
import { PillTabs } from "@/shared/components/PillTabs";
import { EmptyState } from "@/shared/components/EmptyState";
import { formatRelativeTime } from "@/shared/lib/formatRelativeTime";
import { toastError } from "@/shared/lib/toast";
import { ISSUE_LABELS } from "@/features/patient/medications/components/medicationReviewBadges";
import { claimReviewItemApi, listReviewQueueApi, type QueueItem } from "@/portals/physician/api/reviewQueueApi";
import { physicianQueryKeys } from "@/portals/physician/api/physicianQueryKeys";
import { useCachedQuery } from "@/shared/hooks/useCachedQuery";
import { usePhysicianReviewLive } from "../hooks/usePhysicianReviewLive";
import { MINE_EMPTY, MyItemsTable, useMyReviewItems } from "./MyItemsTable";

export const CLAIM_CONFLICT_MESSAGE = "Another physician already claimed this item.";
export const QUEUE_EMPTY = "No items waiting in the shared queue.";
export { MINE_EMPTY };

type TabId = "queue" | "mine";

interface PhysicianDashboardViewProps {
  onOpenItem: (id: string) => void;
}

export function PhysicianDashboardView({ onOpenItem }: PhysicianDashboardViewProps) {
  const [tab, setTab] = useState<TabId>("queue");
  const [claimingId, setClaimingId] = useState<string | null>(null);

  // Cached: coming back from an item shows both lists at once, then refreshes them quietly.
  const queueQuery = useCachedQuery<QueueItem[], { message?: string } | null>(
    physicianQueryKeys.queue,
    async () => {
      const { data, error } = await listReviewQueueApi();
      return { data: data?.items ?? null, error };
    },
    { onError: (error) => toastError(error?.message ?? "Unable to load the shared queue.") },
  );
  const mineQuery = useMyReviewItems();
  const queue = queueQuery.data ?? [];
  const mine = mineQuery.data ?? [];
  const queueLoading = queueQuery.loading;
  const mineLoading = mineQuery.loading;
  const loadQueue = queueQuery.refresh;
  const loadMine = mineQuery.refresh;

  usePhysicianReviewLive(() => void loadQueue(), () => void loadMine());

  const handleClaim = async (item: QueueItem) => {
    setClaimingId(item.id);
    const { data, error } = await claimReviewItemApi(item.id);
    setClaimingId(null);
    if (data) {
      queueQuery.mutate((prev = []) => prev.filter((i) => i.id !== item.id));
      onOpenItem(data.id);
      return;
    }
    toastError(error?.code === "ITEM_ALREADY_CLAIMED" ? CLAIM_CONFLICT_MESSAGE : error?.code === "REVIEW_ITEM_NOT_FOUND" ? "This item isn't available." : error?.message ?? "Unable to claim this item.");
    void loadQueue();
  };

  const queueColumns: ColumnsType<QueueItem> = [
    { title: "Issue", key: "issue", render: (_, i) => <span className="font-semibold text-[#0A2947]">{ISSUE_LABELS[i.triggerType]}</span> },
    { title: "Medicine", dataIndex: "medicineName", key: "medicineName" },
    { title: "Records", dataIndex: "recordCount", key: "recordCount", width: 100 },
    { title: "Waiting since", key: "createdAt", render: (_, i) => formatRelativeTime(i.createdAt) },
    {
      title: "",
      key: "claim",
      align: "right",
      render: (_, i) => (
        <Button
          busy={claimingId === i.id}
          disabled={claimingId !== null && claimingId !== i.id}
          onClick={() => void handleClaim(i)}
          className="!h-8 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-4 !text-xs !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white"
        >
          Claim
        </Button>
      ),
    },
  ];

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
        <div className="mb-5 flex justify-center">
          <PillTabs
            label="Review queue view"
            active={tab}
            onChange={setTab}
            tabs={[
              { id: "queue", label: `Shared queue (${queue.length})` },
              { id: "mine", label: `My items (${mine.length})` },
            ]}
          />
        </div>

        {tab === "queue" ? (
          <Table<QueueItem>
            rowKey="id"
            columns={queueColumns}
            dataSource={queue}
            loading={queueLoading}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            locale={{ emptyText: <EmptyState variant="compact" title={QUEUE_EMPTY} /> }}
            scroll={{ x: 720 }}
          />
        ) : (
          <MyItemsTable items={mine} loading={mineLoading} onOpenItem={onOpenItem} />
        )}
      </div>
    </div>
  );
}
